import { Component, ViewEncapsulation, OnInit, inject } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { lastValueFrom } from 'rxjs';

import { ConciergeApiService } from 'src/shared/services/concierge-api.service';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { Util } from 'src/shared/util/util';
import { WindowService } from 'src/shared/providers/window.service';

import { TaskPage } from 'src/pages/tasks/task/task';

import { AttachmentModel, ConciergeCommentModel, ConciergeItemModel } from 'src/models/concierge.model';

@Component({
    templateUrl: 'concierge-reply.html',
    styleUrls: ['./concierge-reply.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})

export class ConciergeReplyPage implements OnInit {

  errorMessage: string;
  email: string;
  form: UntypedFormGroup;
  item: ConciergeItemModel;
  statusIcon: string | undefined;
  submitted: boolean;
  attachments: AttachmentModel[] = [];
  comments: ConciergeCommentModel[] = [];
  isRightSideContent = true;
  dialogParams: any;
  loaded = false;

  boundSend = this.send.bind(this);

  private readonly conciergeApi = inject(ConciergeApiService);
  private readonly fb = inject(UntypedFormBuilder);
  private readonly navCtrl = inject(CustomNavController);
  private readonly rightSidePanel = inject(RightSidePanelService);
  private readonly storage = inject(TidyStorage);
  private readonly util = inject(Util);
  readonly windowService = inject(WindowService);

  constructor() {
    this.form = this.fb.group({
      content: ['', Validators.required]
    });
  }

  async ngOnInit() {
    this.loaded = false;
    this.isRightSideContent = await this.storage.retrieve('dialog-right-side-open') || false;
    this.rightSidePanel.setDialogPageTitle('Create Action Plan');
    if (this.windowService.isDesktopRes) {
      this.rightSidePanel.setDialogPageTitle('Concierge Action')
      this.dialogParams = await this.storage.retrieve('dialog-params');
    }
    // Dialog params only apply inside the side panel; a full page (e.g. the ticket opened from the email link) passes the id through nav params, even on desktop.
    const itemId = this.isRightSideContent ? this.dialogParams?.itemId : this.navCtrl.getParam('itemId');
    if (itemId) {
      this.item = await lastValueFrom(this.conciergeApi.getConciergeItemById(itemId));
      this.comments = this.item?.recent_comments || [];
    }
    this.statusIcon = this.conciergeApi.getStatusIcon(this.item?.components?.[0]?.value?.status);
    this.loaded = true;
  }

  async send() {
    this.errorMessage = '';
    this.submitted = true;
    if (!this.form.valid) {
      return;
    }
    const data = {
      support_ticket_id: this.item.metadata.support_ticket_id,
      content: this.form.value.content,
      attachments: this.attachments.map(item => item.fileKey)
    };
    try {
      await lastValueFrom(this.conciergeApi.addTicketComment(data));
      const successMessage = 'Reply sent';
      this.util.showSuccess(successMessage);
      // Only the maintenance task flow passes an issue; the task page can't load without it.
      const issue = this.isRightSideContent ? this.dialogParams?.issue : this.navCtrl.getParam('issue');
      if (!issue) {
        this.navCtrl.navigateBack(`concierge-message/${this.item.id}`);
      } else if (this.windowService.isDesktopRes) {
        this.rightSidePanel.openRightSidePanel(TaskPage);
      } else {
        this.navCtrl.navigateForward('task')
      }
    } catch (err) {
      this.errorMessage = err.error ? err.error.message : err.message;
    }
  }

  async addAttachment() {
    this.errorMessage = '';
    try {
      const attachment = await this.conciergeApi.addAttachment();
      this.attachments.push(attachment);
    } catch (err) {
      this.errorMessage = err;
    }
  }

  removeAttachment(attachIndex: number) {
    this.attachments.splice(attachIndex, 1);
  }

}
